<template>
  <div>
    <ModalMessage :visible="showSuccessMessage" :title="successTitle" :messages="successMessages"
      @close="showSuccessMessage = false" />

    <!-- New/update Modal -->
    <div v-if="showModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4 pt-20"
      @click="closeModal">
      <div class="bg-white p-6 rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto" @click.stop>
        <h2 class="text-xl mb-4">{{ isEditing ? 'Modifica' : 'Aggiungi' }} rapportino</h2>
        <form @submit.prevent="isEditing ? update() : add()">
          <div class="mb-4">
            <label class="block text-gray-700">Cliente</label>
            <v-select :options="foundClients" :filterable="false" v-model="form.client" label="name"
              @search="searchClients" @option:selected="onClientSelect" required>
              <template #no-options="{ search, searching }">
                <div v-if="searching">🔍 Ricerca in corso...</div>
                <div v-else-if="!search">Inizia a digitare per cercare un cliente...</div>
              </template>
            </v-select>
            <div v-if="errors.client_id" class="text-red-500 text-sm pt-2">{{ errors.client_id }}</div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700">Sede contratto</label>
            <select v-model="form.branch_id" class="p93__input w-full" :disabled="!form.client_id">
              <option value="" :selected="form.branch_id === ''">Sede principale</option>
              <option v-for="site in sites" :key="site.id" :value="site.id" :selected="form.branch_id === site.id">{{ site.name }}</option>
            </select>
            <div v-if="errors.branch_id" class="py-4 text-red-800">{{ errors.branch_id }}</div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700">Tecnico</label>
            <select v-model="form.owner_id" class="p93__input w-full" required>
              <option value="" selected>Seleziona un tecnico</option>
              <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
            </select>
            <div v-if="errors.owner_id" class="text-red-500 text-sm pt-2">{{ errors.owner_id }}</div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700">Data intervento</label>
            <VueDatePicker v-model="form.intervention_date" locale="it" format="dd/MM/yyyy" auto-apply
              :enable-time-picker="false" :position="'bottom'"
              class="w-full border border-neutral-300 rounded-md shadow-md" />
            <div v-if="form.errors.intervention_date" class="text-red-500 text-sm">{{ form.errors.intervention_date }}
            </div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700">Tipo intervento</label>
            <input type="text" v-model="form.intervention_type" class="p93__input w-full" required>
            <div v-if="form.errors.intervention_type" class="text-red-500 text-sm">{{ form.errors.intervention_type }}
            </div>
          </div>
          <!-- Ora arrivo e Ora partenza -->
          <div class="mb-4">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-gray-700">Ora arrivo</label>
                <input type="time" v-model="form.arrival_time" class="p93__input w-full" required>
                <div v-if="form.errors.arrival_time" class="text-red-500 text-sm">{{ form.errors.arrival_time }}</div>
              </div>
              <div>
                <label class="block text-gray-700">Ora partenza</label>
                <input type="time" v-model="form.departure_time" class="p93__input w-full" required>
                <div v-if="form.errors.departure_time" class="text-red-500 text-sm">{{ form.errors.departure_time }}
                </div>
              </div>
            </div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700">Descrizione intervento</label>
            <textarea v-model="form.description" class="p93__input w-full"></textarea>
            <div v-if="errors.description" class="text-red-500 text-sm pt-2">{{ errors.description }}</div>
          </div>
          <div class="mb-4">
            <label class="block text-gray-700">Materiale utilizzato</label>
            <textarea v-model="form.material" class="p93__input w-full"></textarea>
            <div v-if="errors.material" class="text-red-500 text-sm pt-2">{{ errors.material }}</div>
          </div>

          <!-- Firme Cliente e Tecnico -->
          <div class="mb-4">
            <div class="grid grid-cols-2 gap-4">
              <!-- Firma Cliente -->
              <div>
                <label class="block text-gray-700">Firma Cliente</label>
                <div @click="openSignatureModal('client')"
                  class="w-full h-32 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-gray-400 transition-colors flex items-center justify-center bg-gray-50">
                  <div v-if="form.client_signature" class="w-full h-full relative">
                    <img :src="form.client_signature" alt="Firma Cliente" class="w-full h-full object-contain" />
                    <div
                      class="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all flex items-center justify-center">
                      <span class="text-white opacity-0 hover:opacity-100 transition-opacity">Clicca per
                        modificare</span>
                    </div>
                  </div>
                  <div v-else class="text-gray-500 text-center">
                    <svg class="mx-auto h-8 w-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <p>Clicca per firmare</p>
                  </div>
                </div>
                <div v-if="errors.client_signature" class="text-red-500 text-sm pt-2">{{ errors.client_signature }}
                </div>
              </div>

              <!-- Firma Tecnico -->
              <div>
                <label class="block text-gray-700">Firma Tecnico</label>
                <div @click="openSignatureModal('technician')"
                  class="w-full h-32 border-2 border-dashed border-gray-300 rounded-md cursor-pointer hover:border-gray-400 transition-colors flex items-center justify-center bg-gray-50">
                  <div v-if="form.technician_signature" class="w-full h-full relative">
                    <img :src="form.technician_signature" alt="Firma Tecnico" class="w-full h-full object-contain" />
                    <div
                      class="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all flex items-center justify-center">
                      <span class="text-white opacity-0 hover:opacity-100 transition-opacity">Clicca per
                        modificare</span>
                    </div>
                  </div>
                  <div v-else class="text-gray-500 text-center">
                    <svg class="mx-auto h-8 w-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                    <p>Clicca per firmare</p>
                  </div>
                </div>
                <div v-if="errors.technician_signature" class="text-red-500 text-sm pt-2">{{ errors.technician_signature
                  }}</div>
              </div>
            </div>
          </div>
          <div class="flex justify-end gap-3">
            <button type="button" @click="closeModal" class="p93__btn_grey">Annulla</button>
            <button type="submit" class="p93__btn_primary">{{ isEditing ?
              'Aggiorna' : 'Aggiungi'
              }}</button>
          </div>
          <input type="hidden" v-model="form.id">
        </form>
      </div>
    </div>

    <!-- Modal Firma Universale -->
    <div v-if="showSignatureModal"
      class="fixed inset-0 bg-gray-900 bg-opacity-95 flex items-center justify-center z-50">
      <div class="bg-white p-6 rounded-md w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl mb-4 text-center">{{ currentSignatureType === 'client' ? 'Firma Cliente' : 'Firma Tecnico' }}
        </h2>
        <div class="mb-4">
          <VueSignaturePad ref="signaturePad" width="100%" height="400px" :options="{ penColor: 'black' }"
            class="rounded-md shadow-lg border" />
        </div>
        <div class="flex justify-between">
          <button @click="clearSignature" type="button" class="p93__btn_red flex">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Cancella firma
          </button>
          <div class="flex gap-3">
            <button @click="closeSignatureModal" type="button" class="p93__btn_grey">
              Annulla
            </button>
            <button @click="saveSignature" type="button" class="p93__btn_primary flex">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              Salva firma
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="p-6 pb-0">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="p-6 pb-0">
      <div class="flex justify-between items-center mb-4">
        <button @click="openAddModal"
          class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
          Nuovo Rapportino
        </button>
      </div>

      <PageServerTable :headers="headers" :items="items" :items-per-page.sync="ipp" :items-per-page-options="ippo"
        :sort-by="sortby" :items-length="count" :reload-fn="reload" :item-value="'Item'" :page="page" show-current-page>
        <template #item.Item="{ item }">
          <span>'{{ item.Item }}'</span>
        </template>
        <template #item.select="{ item }">
          <button class="p93__btn_yellow" @click="openModifyForm(item)">Modifica</button>
          <button class="p93__btn_primary" @click="openPrintModal(item)" title="Stampa PDF">
            <v-icon name="co-print" />
          </button>
        </template>
        <template #item.intervention_date="{ item }">
          <span>{{ item.intervention_date ? formatDate(item.intervention_date) : '-' }}</span>
        </template>
      </PageServerTable>
    </div>
  </div>

</template>

<script>
import { Link, router, useForm } from '@inertiajs/vue3';
import PageServerTable from '@/Components/PageServerTable.vue';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ModalMessage from '@/Components/ModalMessage.vue';
import ConfirmationModal from '@/Components/ConfirmationModal.vue';
import { VueSignaturePad } from 'vue-signature-pad';
import { useLoader } from '@/Composables/useLoader.js';
import dateTimeMixin from '@/mixins/dateTimeMixin.js';
import axios from 'axios';

export default {
  props: ['errors', 'items', 'users', 'count', 'ippo'],
  mixins: [dateTimeMixin],
  components: {
    Link,
    PageServerTable,
    BreadCrumbs,
    ModalMessage,
    ConfirmationModal,
    VueSignaturePad
  },
  setup() {
    const { withInertiaLoader } = useLoader();
    return { withInertiaLoader };
  },
  data() {
    return {
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Rapportini di intervento', href: route('modules.home', 'rapportini-intervento') }
      ],
      search: this.filters?.search || "",
      headers: [],
      sortby: [],
      ipp: 10,
      page: 1,
      isLoading: false,
      showSuccessMessage: false,
      successTitle: '',
      successMessages: [],
      openActionMenu: null,
      selectedItem: null,
      showDeleteConfirmation: false,
      showModal: false,
      isCreating: false,
      form: useForm({
        id: null,
        client_id: null,
        client_description: '',
        description: '',
        intervention_date: '',
        arrival_time: '',
        departure_time: '',
        material: '',
        client: { id: null, name: '' },
        owner_id: '',
        client_signature: null,
        technician_signature: null,
        intervention_type: '',
        branch_id: '', // Aggiunto per la sede del contratto
      }),
      isEditing: false,
      detailsData: {},
      foundClients: [],
      sites: [],
      showSignatureModal: false,
      currentSignatureType: null,
    };
  },
  methods: {
    reload(sortby = undefined, itemsperpage = undefined, page = undefined, afterdeletion = false) {

      if (sortby) this.sortby = sortby;
      if (itemsperpage) this.ipp = itemsperpage;
      if (page) this.page = page;
      router.post(window.location.href, { headers: this.headers, sortby: this.sortby, itemsperpage: this.ipp, page: this.page }, { only: ['items', 'count'] });
    },
    handleClickOutside(event) {
      // Chiude il menu se si clicca fuori dal pulsante e dal menu, ma non durante la conferma di eliminazione
      if (this.openActionMenu && !this.showDeleteConfirmation && !event.target.closest('.action-menu-trigger') && !event.target.closest('.action-menu')) {
        this.closeActionMenu();
      }
    },

    closeActionMenu() {
      this.openActionMenu = null;
    },

    openAddModal() {
      this.form.clearErrors();
      this.form.id = null;
      this.form.client = { id: null, name: '' };
      this.form.client_id = null;
      this.form.client_description = '';
      this.form.owner_id = '';
      this.form.client_signature = null;
      this.form.technician_signature = null;
      this.form.intervention_date = '';
      this.form.arrival_time = '';
      this.form.departure_time = '';
      this.form.material = '';
      this.form.intervention_type = '';
      this.form.branch_id = ''; // Reset sede contratto
      this.form.description = '';

      // Reset arrays per il nuovo form
      this.foundClients = [];

      this.isEditing = false;
      this.showModal = true;
    },

    openModifyForm(item) {
      this.form.clearErrors();
      this.form.id = item.id;
      this.form.client = { id: item.client_id, name: item.company_name || (item.client ? item.client.name : '') };
      this.form.client_id = item.client_id;
      this.form.client_description = item.company_name || (item.client ? item.client.name : '');
      this.form.description = item.description || '';
      this.form.intervention_date = item.intervention_date || '';
      this.form.intervention_type = item.intervention_type || '';
      this.form.arrival_time = item.arrival_time || '';
      this.form.departure_time = item.departure_time || '';
      this.form.material = item.material || '';
      this.form.owner_id = item.owner ? item.owner.id : (item.owner_id || '');
      this.form.client_signature = item.client_signature || null;
      this.form.technician_signature = item.technician_signature || null;
      this.form.branch_id = item.branch_id || '';

      this.foundClients = [this.form.client];

      this.onClientSelect({'id': this.form.client.id, 'name': this.form.client.name}, false);

      this.isEditing = true;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.showSignatureModal = false;
      this.currentSignatureType = null;
    },

    searchClients(search, loading) {
      if (search.length >= 2) {
        loading(true);
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          axios
            .get(route('modules.contratti.ricerca-clienti', search))
            .then((response) => {
              this.foundClients = response.data;
              loading(false);
            });
        }, 300);
      }
    },

    onClientSelect(selectedClient) {
      this.form.client_description = selectedClient ? selectedClient.name : '';
      this.form.client_id = selectedClient ? selectedClient.id : '';
      this.getSites(selectedClient);
    },

    getSites(clientId) {
      axios.get(route('modules.contratti.sedi', clientId))
        .then((response) => {
          this.sites = response.data.map(item => {
            const company = item.CompanyName ? item.CompanyName + " - " : "";
            const name = `${company}${item.Address} ${item.ZIPCode} ${item.City} ${item.County}`;
            return {
              id: item.id,
              name
            };
          });
        });
    },

    async add() {
      try {
        const result = await this.withInertiaLoader(
          this.form,
          route('modules.rapporti-intervento.aggiungi'),
          {
            onSuccess: () => {
              this.successTitle = 'Successo';
              this.successMessages = ['Intervento registrato con successo'];
              this.showSuccessMessage = true;
              this.showActivityModal = false;
            },
          },
          'Creazione in corso...'
        );
      } catch (error) {
        console.error('Errore durante la creazione:', error);
        this.successTitle = 'Errore';
        this.successMessages = ['Si è verificato un errore durante la creazione dell\'intervento'];
        this.showSuccessMessage = true;
        this.showActivityModal = false;
      }
    },

    async update() {
      try {
        const result = await this.withInertiaLoader(
          this.form,
          route('modules.rapporti-intervento.modifica'),
          {
            onSuccess: () => {
              this.successTitle = 'Successo';
              this.successMessages = ['Rapportino aggiornato con successo'];
              this.showSuccessMessage = true;
              this.showModal = false;
            },
          },
          'Aggiornamento in corso...',
          'post'
        );
      } catch (error) {
        console.error('Errore durante l\'aggiornamento:', error);
        this.successTitle = 'Errore';
        this.successMessages = ['Si è verificato un errore durante l\'aggiornamento'];
        this.showSuccessMessage = true;
        this.showModal = false;
      }
    },

    // Metodi per gestire le firme
    openSignatureModal(type) {
      this.currentSignatureType = type;
      this.showSignatureModal = true;
      this.$nextTick(() => {
        const existingSignature = type === 'client' ? this.form.client_signature : this.form.technician_signature;
        if (existingSignature && this.$refs.signaturePad) {
          // Se c'è già una firma, caricala nel pad
          this.$refs.signaturePad.fromDataURL(existingSignature);
        }
      });
    },

    closeSignatureModal() {
      this.showSignatureModal = false;
      this.currentSignatureType = null;
    },

    clearSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
      }
    },

    saveSignature() {
      if (this.$refs.signaturePad) {
        const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
        if (!isEmpty) {
          if (this.currentSignatureType === 'client') {
            this.form.client_signature = data;
          } else if (this.currentSignatureType === 'technician') {
            this.form.technician_signature = data;
          }
          this.closeSignatureModal();
        } else {
          alert('Per favore, inserisci una firma prima di salvare.');
        }
      }
    },

    openPrintModal(item) {
      // Apre la stampa PDF in una nuova finestra
      const printUrl = route('modules.rapporti-intervento.stampa', item.id);
      window.open(printUrl, '_blank');
    },
  },
  mounted() {
    this.headers = this.$attrs.headers;
    this.ipp = this.$attrs.ipp;
    this.page = this.$attrs.page;
    this.sortby = this.$attrs.sortby;

    // Chiude il menu quando si clicca fuori
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('resize', this.closeActionMenu);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>